/**
 * SavedSetsBar — save, share and pick up a named snapshot of the settings it sits inside.
 *
 * Deliberately embedded inside the group it belongs to (Roster Generation Settings
 * or Shortcuts), not a standalone section — a bar titled "Saved" next to the
 * settings it captures says what is saved without a separate screen to explain it.
 * The two kinds behave differently on adopt (a generation set loads into settings
 * for the next roster; a shortcut set switches over immediately), which the
 * group's own subtitle states, so this bar carries no explanatory text at all —
 * only names, a save box, and hover tooltips for the rest.
 */

import { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/api/client";
import { useNotification } from "@/contexts/NotificationContext";
import type { ConfigSetKind, ConfigSetSummary } from "@/types/api";

function formatWhen(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
}

function chipTooltip(set: ConfigSetSummary): string {
  const who = set.mine ? "Yours" : `Shared by ${set.origin_session_id ?? "another session"}`;
  return [who, formatWhen(set.created_at)].filter(Boolean).join(" · ");
}

function SavedChip({
  set,
  isPinned,
  busy,
  onAdopt,
  onShare,
}: {
  set: ConfigSetSummary;
  isPinned: boolean;
  busy: boolean;
  onAdopt: () => void;
  onShare: (shared: boolean) => void;
}) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
      <button
        onClick={onAdopt}
        disabled={busy}
        title={chipTooltip(set)}
        style={{
          padding: "4px 10px",
          borderRadius: 14,
          fontSize: 12,
          fontWeight: 600,
          cursor: busy ? "not-allowed" : "pointer",
          border: `1px solid ${isPinned ? "#667eea" : "#cbd5e1"}`,
          background: isPinned ? "#667eea" : "white",
          color: isPinned ? "white" : "#334155",
          whiteSpace: "nowrap",
        }}
      >
        {isPinned && "✓ "}{set.name}
      </button>
      {set.mine && (
        <button
          onClick={() => onShare(!set.shared)}
          disabled={busy}
          title={set.shared ? "Shared with all sessions — click to make private" : "Private — click to share"}
          style={{
            padding: "4px 6px",
            borderRadius: 10,
            fontSize: 11,
            border: `1px solid ${set.shared ? "#667eea" : "#e2e8f0"}`,
            background: set.shared ? "#eef2ff" : "white",
            color: set.shared ? "#667eea" : "#94a3b8",
            cursor: busy ? "not-allowed" : "pointer",
          }}
        >
          {set.shared ? "🔗" : "🔒"}
        </button>
      )}
    </span>
  );
}

export function SavedSetsBar({ kind, embedded = false }: { kind: ConfigSetKind; embedded?: boolean }) {
  const { notifySuccess, notifyWarning, notifyError } = useNotification();
  const [sets, setSets] = useState<ConfigSetSummary[]>([]);
  const [pinnedId, setPinnedId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const body = await apiClient.configSets.list(kind);
      setSets(body.sets);
      setPinnedId(body.pinned_id);
    } catch (err) {
      notifyError((err as Error).message || "Failed to load saved configurations.");
    } finally {
      setLoaded(true);
    }
  }, [kind, notifyError]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      notifyWarning("Give it a name first.");
      return;
    }
    setBusy(true);
    try {
      await apiClient.configSets.save(kind, trimmed);
      setName("");
      notifySuccess(`Saved "${trimmed}".`);
      await load();
    } catch (err) {
      notifyError((err as Error).message || "Failed to save.");
    } finally {
      setBusy(false);
    }
  };

  const handleAdopt = async (set: ConfigSetSummary) => {
    setBusy(true);
    try {
      const result = await apiClient.configSets.adopt(set.id);
      // The message comes from the server rather than being rewritten here,
      // because what adopting *does* is decided there and the two must agree.
      notifySuccess(`"${set.name}" — ${result.message}`);
      await load();
    } catch (err) {
      notifyError((err as Error).message || "Failed to adopt.");
    } finally {
      setBusy(false);
    }
  };

  const handleShare = async (set: ConfigSetSummary, shared: boolean) => {
    setBusy(true);
    try {
      const result = await apiClient.configSets.share(set.id, shared);
      if (!shared && result.in_use_by.length > 0) {
        notifyWarning(
          `Hidden from other sessions. ${result.in_use_by.length} session(s) already using it keep it.`,
        );
      } else {
        notifySuccess(shared ? "Shared with all sessions." : "No longer shared.");
      }
      await load();
    } catch (err) {
      notifyError((err as Error).message || "Failed to change sharing.");
    } finally {
      setBusy(false);
    }
  };

  // Embedded mode drops the card chrome (background/border) so this reads as
  // part of the group header it sits inside, not a separate box below it.
  const containerStyle: React.CSSProperties = embedded
    ? {
        background: "rgba(255,255,255,0.6)",
        border: "1px solid #c7d2fe",
        borderRadius: 8,
        padding: "8px 10px",
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
        alignItems: "center",
      }
    : {
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: 8,
        padding: "9px 10px",
        marginBottom: 16,
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
        alignItems: "center",
      };

  const labelColor = embedded ? "#4c51bf" : "#64748b";
  const mutedColor = embedded ? "#6366f1" : "#94a3b8";

  return (
    <div style={containerStyle}>
      <span style={{ fontSize: 10, fontWeight: 700, color: labelColor, textTransform: "uppercase", letterSpacing: 0.5 }}>
        Saved
      </span>

      {!loaded ? (
        <span style={{ fontSize: 12, color: mutedColor }}>…</span>
      ) : sets.length === 0 ? (
        <span style={{ fontSize: 12, color: mutedColor }}>None yet</span>
      ) : (
        sets.map((set) => (
          <SavedChip
            key={set.id}
            set={set}
            isPinned={set.id === pinnedId}
            busy={busy}
            onAdopt={() => void handleAdopt(set)}
            onShare={(shared) => void handleShare(set, shared)}
          />
        ))
      )}

      <span style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && void handleSave()}
          placeholder="Save current as…"
          disabled={busy}
          style={{
            padding: "5px 9px",
            border: "1px solid #d1d5db",
            borderRadius: 6,
            fontSize: 12,
            width: 150,
          }}
        />
        <button
          onClick={() => void handleSave()}
          disabled={busy}
          style={{
            padding: "5px 12px",
            background: busy ? "#9ca3af" : "#10b981",
            color: "white",
            border: "none",
            borderRadius: 6,
            cursor: busy ? "not-allowed" : "pointer",
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          Save
        </button>
      </span>
    </div>
  );
}
